import React, { useRef, useCallback } from "react";
import {
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  VStack,
  Box,
  Text,
  Tooltip,
} from "@chakra-ui/react";
import { QRCode } from "react-qrcode-logo";

import urzzaLogo from "../../../../components/icons/urzza-icon.png";

import qrIcon from "../../../../assets/img/chargePointsTab/qr.png";

const QrCode = ({ value, compact = false }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const qrRef = useRef(null);

  const handleDownload = useCallback(() => {
    const canvas = qrRef.current?.querySelector("canvas");
    if (!canvas) return;

    // Create a high-resolution canvas (4x scale for printing)
    const scale = 4;
    const highResCanvas = document.createElement("canvas");
    const ctx = highResCanvas.getContext("2d");

    highResCanvas.width = canvas.width * scale;
    highResCanvas.height = canvas.height * scale;

    // Enable image smoothing for better quality
    ctx.imageSmoothingEnabled = false;

    // Draw the original canvas scaled up
    ctx.drawImage(canvas, 0, 0, highResCanvas.width, highResCanvas.height);

    // Download the high-res image
    const link = document.createElement("a");
    link.download = `urzza-qrcode-${value || "code"}-highres.png`;
    link.href = highResCanvas.toDataURL("image/png", 1.0);
    link.click();
  }, [value]);

  return (
    <div>
      {compact ? (
        <Tooltip label="View / Download QR Code" placement="top" hasArrow bg="#f57d36" color="white">
          <Button
            onClick={onOpen}
            size="sm"
            w="100%"
            borderRadius="lg"
            bg="rgba(245, 125, 54, 0.08)"
            color="#f57d36"
            border="1px solid rgba(245, 125, 54, 0.28)"
            leftIcon={
              <Box
                as="span"
                display="flex"
                alignItems="center"
                justifyContent="center"
                w={{ base: "16px", md: "20px" }}
                h={{ base: "16px", md: "20px" }}
                borderRadius="5px"
                bg="linear-gradient(135deg, #f57d36 0%, #ff9966 100%)"
                color="white"
                flexShrink={0}
              >
                <svg
                  width="10"
                  height="10"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path d="M3 3h7v7H3zm1 1v5h5V4zm1 1h3v3H5zM14 3h7v7h-7zm1 1v5h5V4zm1 1h3v3h-3zM3 14h7v7H3zm1 1v5h5v-5zm1 1h3v3H5zM17 14h2v2h-2zm-3 0h2v2h-2zm0 3h2v2h-2zm3 0h2v2h-2zm3-3h1v7h-1v-4h-1v-1h1zm-4 4h1v1h-1zm1 2h1v1h-1z"/>
                </svg>
              </Box>
            }
            _hover={{
              bg: "rgba(245, 125, 54, 0.15)",
              borderColor: "rgba(245, 125, 54, 0.5)",
              transform: "translateY(-1px)",
              boxShadow: "0 4px 12px rgba(245, 125, 54, 0.18)",
            }}
            _active={{ bg: "rgba(245, 125, 54, 0.2)", transform: "translateY(0)" }}
            transition="all 0.2s ease"
            h={{ base: "28px", md: "32px" }}
            fontWeight="600"
            fontSize="xs"
            letterSpacing="0.3px"
            px={{ base: 1.5, md: 3 }}
            overflow="hidden"
            minW={0}
          >
            <Box as="span" display={{ base: "none", md: "inline" }}>QR Code</Box>
          </Button>
        </Tooltip>
      ) : (
        <Button
          colorScheme="red"
          bg="#ff4961"
          _hover={{ bg: "#e63e54" }}
          onClick={onOpen}
          borderRadius={5}
          w={"full"}
          className="acCharger_button qr"
          leftIcon={
            <img
              src={qrIcon}
              alt="qr"
              style={{ display: "inline", marginRight: "0.44rem", width: "25px" }}
            />
          }
          fontStyle={"normal"}
          fontWeight={"400"}
          fontFamily={"Roboto"}
        >
          QR Code
        </Button>
      )}

      <Modal
        isOpen={isOpen}
        onClose={onClose}
        isCentered
        motionPreset="slideInBottom"
        size="md"
      >
        <ModalOverlay bg="blackAlpha.300" backdropFilter="blur(10px)" />
        <ModalContent
          bg="linear-gradient(135deg, #fff8f3 0%, #ffffff 40%, #fff5ed 100%)"
          mx={4}
          borderRadius="2xl"
          boxShadow="0 20px 60px rgba(255, 92, 0, 0.15), 0 0 0 1px rgba(255, 92, 0, 0.08)"
          p={3}
          overflow="hidden"
          position="relative"
        >
          <Box
            position="absolute"
            top="-50px"
            right="-50px"
            w="150px"
            h="150px"
            borderRadius="full"
            bg="radial-gradient(circle, rgba(255,92,0,0.12) 0%, transparent 70%)"
            pointerEvents="none"
          />
          <Box
            position="absolute"
            bottom="-30px"
            left="-30px"
            w="120px"
            h="120px"
            borderRadius="full"
            bg="radial-gradient(circle, rgba(245,125,54,0.1) 0%, transparent 70%)"
            pointerEvents="none"
          />
          <ModalHeader
            fontSize="lg"
            fontWeight="bold"
            color="#ff5c00"
            position="relative"
          >
            QR Code
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6} position="relative">
            <VStack spacing={8} pt={8} pb={4}>
              <Box position="relative">
                {/* Branded corner decorations */}
                <Box
                  position="absolute"
                  top="-8px"
                  left="-8px"
                  w="60px"
                  h="60px"
                  borderTop="3px solid #ff5c00"
                  borderLeft="3px solid #ff5c00"
                  borderTopLeftRadius="12px"
                  zIndex={1}
                />
                <Box
                  position="absolute"
                  top="-8px"
                  right="-8px"
                  w="60px"
                  h="60px"
                  borderTop="3px solid #ff5c00"
                  borderRight="3px solid #ff5c00"
                  borderTopRightRadius="12px"
                  zIndex={1}
                />
                <Box
                  position="absolute"
                  bottom="-8px"
                  left="-8px"
                  w="60px"
                  h="60px"
                  borderBottom="3px solid #ff5c00"
                  borderLeft="3px solid #ff5c00"
                  borderBottomLeftRadius="12px"
                  zIndex={1}
                />
                <Box
                  position="absolute"
                  bottom="-8px"
                  right="-8px"
                  w="60px"
                  h="60px"
                  borderBottom="3px solid #ff5c00"
                  borderRight="3px solid #ff5c00"
                  borderBottomRightRadius="12px"
                  zIndex={1}
                />

                <Box
                  p={5}
                  bg="linear-gradient(145deg, rgba(255,92,0,0.06) 0%, rgba(245,125,54,0.03) 50%, rgba(255,92,0,0.06) 100%)"
                  borderRadius="xl"
                  border="1px solid rgba(255, 92, 0, 0.12)"
                  boxShadow="inset 0 2px 10px rgba(255, 92, 0, 0.05), 0 4px 15px rgba(0,0,0,0.04)"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  <Box
                    ref={qrRef}
                    p={3}
                    bg="white"
                    borderRadius="lg"
                    boxShadow="0 4px 20px rgba(255, 92, 0, 0.1)"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <QRCode
                      value={value || ""}
                      size={300}
                      ecLevel="H"
                      qrStyle="dots"
                      eyeRadius={8}
                      eyeColor="#ff5c00"
                      fgColor="#ff5c00"
                      bgColor="#ffffff"
                      logoImage={urzzaLogo}
                      logoWidth={30}
                      logoHeight={44}
                      logoOpacity={0.95}
                      removeQrCodeBehindLogo
                      logoPaddingStyle="circle"
                      logoPadding={2}
                    />
                  </Box>
                </Box>
              </Box>
              <Box
                px={4}
                py={2}
                bg="rgba(255, 92, 0, 0.06)"
                borderRadius="lg"
                border="1px solid rgba(255, 92, 0, 0.1)"
                maxW="100%"
              >
                <Text
                  fontSize="sm"
                  color="gray.600"
                  textAlign="center"
                  noOfLines={2}
                  fontFamily="mono"
                  fontWeight="500"
                >
                  {value}
                </Text>
              </Box>
              <Button
                colorScheme="orange"
                bg="linear-gradient(135deg, #ff5c00 0%, #f57d36 100%)"
                onClick={handleDownload}
                width="full"
                size="lg"
                borderRadius="lg"
                _hover={{
                  bg: "linear-gradient(135deg, #e65200 0%, #e06420 100%)",
                  transform: "translateY(-2px)",
                  boxShadow: "0 8px 25px rgba(255, 92, 0, 0.35)",
                }}
                _active={{
                  transform: "translateY(0)",
                }}
                transition="all 0.2s"
                boxShadow="0 4px 15px rgba(255, 92, 0, 0.25)"
                fontWeight="600"
                letterSpacing="0.5px"
              >
                Download PNG
              </Button>
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>
    </div>
  );
};

export default QrCode;
